'use strict';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Grid,
	IconButton,
	MenuItem,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import { Brand } from '@/lenses/uilenses/theme/colors';
import dayjs from 'dayjs';
import { StudySummaryCard } from './StudySummaryCard.jsx';
import { StudyBuilderSectionTitle, studyBuilderGradientTopSx } from './StudyBuilderSectionTitle.jsx';
import {
	buildQuoteSampleBasisDisplay,
	buildStudyBuilderEstimateRequest,
	buildStudyBuilderEstimateRequestFromDraft,
	estimateIndicativeCompletionDate,
	mapStudyBuilderEstimatePresentation,
	resolveEstimatedCostDisplay,
	resolveStudyBuilderEstimateFetchErrorMessage,
} from '../studyBuilder.estimate.js';
import {
	getTierSampleBounds,
	resolveStudySizeId,
} from '../studyBuilder.config.js';
import { useLazyGetStudyBuilderDraftQuery, usePostStudyBuilderDraftMutation, usePostStudyBuilderEstimateMutation, useSendStudyBuilderQuoteSummaryEmailMutation } from '@/redux/services/portal/studyBuilder.api.js';
import { buildStudyBuilderQuoteSummaryPlainText } from '../studyBuilderQuoteSummaryPlainText.js';
import { formatDisplayDate } from '../studyBuilder.dateDisplay.js';
import {
	clearStudyBuilderGuestSession,
	mergeQuoteIntoGuestSession,
	readStudyBuilderGuestSession,
	writeStudyBuilderGuestSession,
} from '../studyBuilderGuestSession.js';
import { writeStudyBuilderDraftToken } from '../studyBuilderDraftToken.js';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated.js';
import { useCurrentUser } from '@/redux/services/portal/user.api.js';
import { useAuth0 } from '@auth0/auth0-react';

function getUserFirstName(user) {
	return user?.firstName || user?.FirstName || '';
}

function getUserLastName(user) {
	return user?.lastName || user?.LastName || '';
}

function getUserEmail(user) {
	return user?.email || user?.Email || '';
}

function contactInfoFromUser(user) {
	return {
		firstName: getUserFirstName(user),
		lastName: getUserLastName(user),
		email: getUserEmail(user),
	};
}

function contactInfoFromAuth0User(auth0User) {
	if (!auth0User) {
		return { firstName: '', lastName: '', email: '' };
	}

	const email = auth0User.email || '';
	let firstName = auth0User.given_name || '';
	let lastName = auth0User.family_name || '';

	if (!firstName && !lastName && auth0User.name && !auth0User.name.includes('@')) {
		const parts = auth0User.name.trim().split(/\s+/);
		firstName = parts[0] || '';
		lastName = parts.slice(1).join(' ') || '';
	}

	if (!firstName && auth0User.nickname && !auth0User.nickname.includes('@')) {
		firstName = auth0User.nickname;
	}

	return { firstName, lastName, email };
}

function mergeContactSources(portalUser, auth0User, draftContact = { firstName: '', lastName: '', email: '' }) {
	const fromPortal = contactInfoFromUser(portalUser);
	const fromAuth0 = contactInfoFromAuth0User(auth0User);
	const email = fromPortal.email || fromAuth0.email || draftContact.email || '';
	let firstName = fromPortal.firstName || fromAuth0.firstName || draftContact.firstName || '';
	let lastName = fromPortal.lastName || fromAuth0.lastName || draftContact.lastName || '';

	// Email is enough to send an estimate; fill a display first name when Portal/Auth0 omit names.
	if (!firstName && email.includes('@')) {
		firstName = email.split('@')[0];
	}

	return { firstName, lastName, email };
}

function hasContactInfo(contactInfo) {
	return Boolean(contactInfo?.email?.includes('@'));
}

function buildApiContact(contactInfo) {
	const fullName = `${contactInfo.firstName} ${contactInfo.lastName}`.trim();
	return {
		fullName,
		email: contactInfo.email.trim(),
		phone: '',
	};
}

function EstimateSubmissionSummary({ lines, testId = 'estimate-submission-summary' }) {
	return (
		<Paper
			elevation={0}
			data-testid={testId}
			sx={{
				p: 2,
				mb: 3,
				borderRadius: 2,
				border: `1px solid ${Brand.precisionGrey}`,
				background: 'rgba(242, 250, 252, 0.6)',
			}}
		>
			<Typography variant="overline" sx={{ fontWeight: 800, color: Brand.molecularBlue, letterSpacing: 1.2 }}>
				What you are submitting
			</Typography>
			<Typography variant="body2" sx={{ mt: 0.5, mb: 1.5, color: Brand.molecularBlue, lineHeight: 1.6 }}>
				Review your study selections and sample logistics before requesting your indicative estimate.
			</Typography>
			<Box component="ul" sx={{ m: 0, pl: 2.25 }}>
				{lines.map(([label, value]) => (
					<Typography component="li" variant="body2" key={label} sx={{ mb: 0.5, color: Brand.darkModeBlue }}>
						<strong>{label}:</strong> {value ?? '—'}
					</Typography>
				))}
			</Box>
		</Paper>
	);
}

function SampleCountCallout({
	totalSamples,
	quoteSampleBasisDisplay = null,
	testId = 'quote-sample-count-callout',
	compact = false,
}) {
	const countDisplay = totalSamples > 0 ? totalSamples.toLocaleString('en-US') : '—';

	return (
		<Paper
			elevation={0}
			data-testid={testId}
			sx={{
				p: compact ? { xs: 1.5, sm: 2 } : { xs: 2, sm: 2.5 },
				mb: compact ? 1.5 : 2,
				borderRadius: 3,
				border: `2px solid ${Brand.molecularBlue}`,
				background: `linear-gradient(135deg, rgba(40, 74, 111, 0.1) 0%, rgba(133, 93, 163, 0.08) 55%, rgba(255, 255, 255, 0.95) 100%)`,
				boxShadow: '0 8px 28px rgba(0, 29, 56, 0.08)',
			}}
		>
			<Typography
				variant="overline"
				sx={{
					display: 'block',
					color: Brand.molecularBlue,
					fontWeight: 800,
					letterSpacing: 1.4,
					fontSize: compact ? '0.68rem' : '0.72rem',
				}}
			>
				Review before requesting your estimate
			</Typography>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'baseline',
					flexWrap: 'wrap',
					gap: 1.5,
					mt: compact ? 0.75 : 1,
				}}
			>
				<Typography
					component="span"
					data-testid={`${testId}-value`}
					sx={{
						fontWeight: 900,
						color: Brand.darkModeBlue,
						fontSize: compact ? { xs: '1.75rem', sm: '2rem' } : { xs: '2.25rem', sm: '2.75rem' },
						lineHeight: 1,
						letterSpacing: '-0.04em',
					}}
				>
					{countDisplay}
				</Typography>
				<Typography
					component="span"
					variant={compact ? 'body2' : 'h6'}
					sx={{ fontWeight: 700, color: Brand.molecularBlue }}
				>
					samples
				</Typography>
			</Box>
			{quoteSampleBasisDisplay ? (
				<Typography
					variant="body2"
					data-testid={`${testId}-basis`}
					sx={{ mt: 1, color: Brand.molecularBlue }}
				>
					Estimate basis: <strong>{quoteSampleBasisDisplay}</strong>
				</Typography>
			) : null}
			{totalSamples <= 0 ? (
				<Typography variant="body2" sx={{ mt: 1, color: Brand.activatedRed, fontWeight: 600 }}>
					Enter sample counts on your matrix lines before requesting an estimate.
				</Typography>
			) : null}
		</Paper>
	);
}

function initialLineRows(fit) {
	if (Array.isArray(fit.sampleLines) && fit.sampleLines.length) {
		const approximateScope = fit.matrixScopeMode === 'unsure' || fit.matrixScopeMode === 'multiple';
		return fit.sampleLines.map((ln, i) => ({
			id: ln.id || `ln-${i + 1}`,
			species: ln.species || '',
			matrix: ln.matrix || '',
			matrixCategory: ln.matrixCategory || '',
			matrixType: ln.matrixType || '',
			count: String(ln.count ?? ''),
			otherNotes: ln.species === 'Other' || approximateScope ? String(ln.otherNotes || '') : '',
		}));
	}
	return [
		{
			id: 'ln-1',
			species: '',
			matrix: '',
			matrixCategory: '',
			matrixType: '',
			count: '',
			otherNotes: '',
		},
	];
}

function defaultSampleInfo() {
	return {
		sampleVolume: '100μl',
		sampleShipping: 'Standard',
		plannedShippingDate: dayjs().add(14, 'day').format('YYYY-MM-DD'),
	};
}

function mergeSampleInfoFromDraft(draft) {
	const base = defaultSampleInfo();
	if (!draft?.sampleInfo || typeof draft.sampleInfo !== 'object') return base;
	return { ...base, ...draft.sampleInfo };
}

function mergeContactInfoFromDraft(draft) {
	const base = { firstName: '', lastName: '', email: '' };
	if (!draft?.contactInfo || typeof draft.contactInfo !== 'object') return base;
	const c = draft.contactInfo;
	return {
		firstName: String(c.firstName || c.FirstName || ''),
		lastName: String(c.lastName || c.LastName || ''),
		email: String(c.email || c.Email || ''),
	};
}

function lineItemsFromQuoteDraft(draft, fitSelections) {
	if (!draft || !Array.isArray(draft.lineItems) || draft.lineItems.length === 0) return null;
	return draft.lineItems.map((ln, i) => ({
		id: ln.id || `ln-${i + 1}`,
		species: ln.species || '',
		matrix: ln.matrix || '',
		matrixCategory: ln.matrixCategory || '',
		matrixType: ln.matrixType || '',
		count: String(ln.count ?? ''),
		otherNotes: String(ln.otherNotes ?? ''),
	}));
}

/**
 * @param {{ fitSelections: Record<string, unknown>, primaryProduct: unknown, onBack: () => void, initialQuoteDraft?: Record<string, unknown>|null, enableGuestSessionPersistence?: boolean }} props
 */
export function QuoteWizard({
	fitSelections,
	primaryProduct,
	onBack,
	initialQuoteDraft = null,
	enableGuestSessionPersistence = false,
}) {
	const history = useHistory();
	const isAuthed = useIsAuthenticated();
	const { user: auth0User, isLoading: auth0IsLoading } = useAuth0();
	const {
		data: currentUser,
		isLoading: currentUserLoading,
		isFetching: currentUserFetching,
		isError: currentUserError,
		isUninitialized: currentUserUninitialized,
		refetch: refetchCurrentUser,
	} = useCurrentUser();
	const [quoteRevealed, setQuoteRevealed] = useState(false);
	const [quoteSummaryEmailSent, setQuoteSummaryEmailSent] = useState(false);
	const [seekEstimateAttempted, setSeekEstimateAttempted] = useState(false);
	const [estimatePresentation, setEstimatePresentation] = useState(null);
	const [estimateFetchErrorMessage, setEstimateFetchErrorMessage] = useState(null);

	const [lineItems, setLineItems] = useState(() => lineItemsFromQuoteDraft(initialQuoteDraft, fitSelections) || initialLineRows(fitSelections));

	const [sampleInfo, setSampleInfo] = useState(() => mergeSampleInfoFromDraft(initialQuoteDraft));

	const [contactInfo, setContactInfo] = useState(() => mergeContactInfoFromDraft(initialQuoteDraft));
	const quoteDraftRef = useRef({ lineItems, sampleInfo, contactInfo });
	const quoteRevealedRef = useRef(quoteRevealed);
	const postLoginResumeRef = useRef(false);
	quoteDraftRef.current = { lineItems, sampleInfo, contactInfo };

	useEffect(() => {
		quoteRevealedRef.current = quoteRevealed;
	}, [quoteRevealed]);

	const totalSamples = useMemo(
		() => lineItems.reduce((sum, row) => sum + (parseInt(row.count, 10) || 0), 0),
		[lineItems]
	);

	const buildEstimateRequestBody = useCallback(
		() => buildStudyBuilderEstimateRequest({
			fitSelections,
			totalSamples,
			quoteDraft: {
				lineItems: quoteDraftRef.current.lineItems,
				sampleInfo: quoteDraftRef.current.sampleInfo,
			},
		}),
		[fitSelections, totalSamples]
	);

	const quoteSampleBasisDisplay = useMemo(() => {
		const display = buildQuoteSampleBasisDisplay(fitSelections, totalSamples);
		return display.includes('(range)') ? display : null;
	}, [
			fitSelections.studySize,
			fitSelections.sampleCountMode,
			fitSelections.exactSampleCount,
			fitSelections.matrixScopeMode,
			totalSamples,
	]);

	const studySizeId = useMemo(
		() => resolveStudySizeId(fitSelections.studySize, totalSamples),
		[fitSelections.studySize, totalSamples]
	);
	const tierBounds = useMemo(() => getTierSampleBounds(studySizeId), [studySizeId]);
	const useTierRangeBasis = quoteSampleBasisDisplay?.includes('(range)') ?? false;

	const estimatedCompletionDisplay = useMemo(
		() => formatDisplayDate(estimateIndicativeCompletionDate(sampleInfo.plannedShippingDate)),
		[sampleInfo.plannedShippingDate]
	);

	const estimatedCostDisplay = quoteRevealed
		? resolveEstimatedCostDisplay(estimatePresentation)
		: null;

	const quotePresentation = quoteRevealed && estimatePresentation
		? {
			...estimatePresentation,
			tierBounds: useTierRangeBasis ? tierBounds : null,
		}
		: null;

	useEffect(() => {
		if (!isAuthed) return;
		const merged = mergeContactSources(currentUser, auth0User, contactInfo);
		if (!hasContactInfo(merged)) return;
		setContactInfo(merged);
	}, [isAuthed, currentUser, auth0User]);

	const submissionSummaryLines = useMemo(() => {
		const rows = [
			['Quoted product', primaryProduct?.name ?? '—'],
			['Samples in this quote', totalSamples > 0 ? String(totalSamples) : '—'],
		];
		if (quoteSampleBasisDisplay) {
			rows.push(['Estimate basis', quoteSampleBasisDisplay]);
		}
		rows.push(['Sample volume', sampleInfo.sampleVolume || '—']);
		rows.push(['Sample shipping', sampleInfo.sampleShipping || '—']);
		rows.push(['Planned shipping date', formatDisplayDate(sampleInfo.plannedShippingDate) || '—']);
		return rows;
	}, [
		primaryProduct,
		totalSamples,
		quoteSampleBasisDisplay,
		sampleInfo.sampleVolume,
		sampleInfo.sampleShipping,
		sampleInfo.plannedShippingDate,
	]);

	const displayContactInfo = mergeContactSources(currentUser, auth0User, contactInfo);
	const contactReady = hasContactInfo(displayContactInfo);
	// Authed but no settled profile yet (skip race, Auth0 still loading, or in-flight fetch)
	// must show loading - not the permanent profile error.
	const isProfileLoading = isAuthed
		&& !currentUserError
		&& !contactReady
		&& (
			auth0IsLoading
			|| currentUserUninitialized
			|| currentUserLoading
			|| currentUserFetching
			|| (!currentUser && !auth0User)
		);
	const isProfileUnavailable = isAuthed && !isProfileLoading && (currentUserError || !contactReady);
	const logisticsOk = Boolean(sampleInfo.sampleVolume && sampleInfo.sampleShipping && sampleInfo.plannedShippingDate);
	const canSeeEstimate = isAuthed ? (contactReady && logisticsOk) : logisticsOk;

	const seekEstimateValidationMessage = useMemo(() => {
		if (!seekEstimateAttempted || canSeeEstimate) return null;

		if (isAuthed && isProfileLoading) {
			return 'Loading your account profile. Try again in a moment.';
		}

		if (isAuthed && isProfileUnavailable) {
			return 'We could not load your account profile. Sign in again to see your estimate.';
		}

		const missingOther = [
			!sampleInfo.sampleVolume && 'sample volume',
			!sampleInfo.sampleShipping && 'sample shipping',
			!sampleInfo.plannedShippingDate && 'planned shipping date',
		].filter(Boolean);

		if (missingOther.length === 1) {
			return `Complete ${missingOther[0]} to see your estimate.`;
		}
		if (missingOther.length > 1) {
			const last = missingOther.pop();
			return `Complete ${missingOther.join(', ')} and ${last} to see your estimate.`;
		}

		return 'Complete the required fields to see your estimate.';
	}, [
		seekEstimateAttempted,
		canSeeEstimate,
		isAuthed,
		isProfileLoading,
		isProfileUnavailable,
		sampleInfo.sampleVolume,
		sampleInfo.sampleShipping,
		sampleInfo.plannedShippingDate,
	]);

	useEffect(() => {
		if (canSeeEstimate) setSeekEstimateAttempted(false);
	}, [canSeeEstimate]);

	const [sendQuoteSummaryEmail, { isLoading: quoteSummaryEmailSending, isError: quoteSummaryEmailError, reset: resetQuoteEmailMutation }] =
		useSendStudyBuilderQuoteSummaryEmailMutation();
	const [postStudyBuilderEstimate, { isLoading: estimateLoading }] = usePostStudyBuilderEstimateMutation();
	const [fetchStudyBuilderDraft] = useLazyGetStudyBuilderDraftQuery();
	const [postStudyBuilderDraft, { isLoading: draftSaving }] = usePostStudyBuilderDraftMutation();
	const prevQuoteRevealedRef = useRef(false);

	const quoteDraftForEmail = useMemo(
		() => ({
			lineItems,
			sampleInfo: { ...sampleInfo, sampleKit: 'no' },
			contactInfo,
		}),
		[lineItems, sampleInfo, contactInfo]
	);

	const buildQuoteEmailPayload = useCallback(() => {
		const plainTextBody = buildStudyBuilderQuoteSummaryPlainText({
			fitSelections,
			primaryProduct,
			draft: quoteDraftForEmail,
			totalSamples,
			estimatedCostDisplay,
			estimatedCompletionDisplay,
			quoteSampleBasisDisplay,
		});
		return {
			toEmail: contactInfo.email.trim(),
			contact: buildApiContact(contactInfo),
			plainTextBody,
		};
	}, [
		fitSelections,
		primaryProduct,
		quoteDraftForEmail,
		totalSamples,
		estimatedCostDisplay,
		estimatedCompletionDisplay,
		quoteSampleBasisDisplay,
		contactInfo.email,
		contactInfo.firstName,
		contactInfo.lastName,
	]);

	const handleRetryQuoteEmail = useCallback(() => {
		resetQuoteEmailMutation();
		sendQuoteSummaryEmail(buildQuoteEmailPayload())
			.unwrap()
			.then(() => setQuoteSummaryEmailSent(true))
			.catch(() => {});
	}, [buildQuoteEmailPayload, resetQuoteEmailMutation, sendQuoteSummaryEmail]);

	const submitEstimateAndReveal = useCallback(() => {
		setEstimateFetchErrorMessage(null);
		return postStudyBuilderEstimate(buildEstimateRequestBody())
			.unwrap()
			.then((response) => {
				setEstimatePresentation(mapStudyBuilderEstimatePresentation(response));
				setQuoteRevealed(true);
			})
			.catch((error) => {
				setEstimateFetchErrorMessage(resolveStudyBuilderEstimateFetchErrorMessage(error));
			});
	}, [buildEstimateRequestBody, postStudyBuilderEstimate]);

	useEffect(() => {
		if (!quoteRevealed) return;
		clearStudyBuilderGuestSession();
	}, [quoteRevealed]);

	useEffect(() => {
		if (!isAuthed || quoteRevealed || postLoginResumeRef.current) return;
		const g = readStudyBuilderGuestSession();
		if (!g?.revealEstimateAfterLogin) return;
		const mergedContact = mergeContactSources(currentUser, auth0User, contactInfo);
		if (!hasContactInfo(mergedContact)) return;
		if (totalSamples < 1) return;

		postLoginResumeRef.current = true;
		setContactInfo(mergedContact);

		const resumeAfterLogin = async () => {
			setEstimateFetchErrorMessage(null);
			try {
				let body;
				try {
					const draft = await fetchStudyBuilderDraft().unwrap();
					body = buildStudyBuilderEstimateRequestFromDraft(draft);
				} catch {
					// Any draft GET failure (not just 404): fall back to restored guest session body.
					body = buildEstimateRequestBody();
				}

				const response = await postStudyBuilderEstimate(body).unwrap();
				setEstimatePresentation(mapStudyBuilderEstimatePresentation(response));
				writeStudyBuilderGuestSession({ revealEstimateAfterLogin: false });
				setQuoteRevealed(true);
			} catch (error) {
				postLoginResumeRef.current = false;
				setEstimateFetchErrorMessage(resolveStudyBuilderEstimateFetchErrorMessage(error));
			}
		};

		resumeAfterLogin();
	}, [
		isAuthed,
		quoteRevealed,
		currentUser,
		auth0User,
		totalSamples,
		buildEstimateRequestBody,
		fetchStudyBuilderDraft,
		postStudyBuilderEstimate,
	]);

	useEffect(() => {
		if (quoteRevealed) return;
		const id = window.setTimeout(() => {
			mergeQuoteIntoGuestSession(quoteDraftRef.current);
			if (enableGuestSessionPersistence) {
				writeStudyBuilderGuestSession({
					selections: fitSelections,
					phase: 'build',
				});
			}
		}, 400);
		return () => {
			window.clearTimeout(id);
			if (!quoteRevealedRef.current) {
				mergeQuoteIntoGuestSession(quoteDraftRef.current);
			}
		};
	}, [
		enableGuestSessionPersistence,
		quoteRevealed,
		fitSelections,
		lineItems,
		sampleInfo,
		contactInfo,
	]);

	useEffect(() => {
		if (!quoteRevealed) {
			prevQuoteRevealedRef.current = false;
			return;
		}
		const firstReveal = !prevQuoteRevealedRef.current;
		prevQuoteRevealedRef.current = true;
		if (!firstReveal || quoteSummaryEmailSent || !estimatePresentation) return;

		sendQuoteSummaryEmail(buildQuoteEmailPayload())
			.unwrap()
			.then(() => setQuoteSummaryEmailSent(true))
			.catch(() => {});
	}, [quoteRevealed, quoteSummaryEmailSent, estimatePresentation, sendQuoteSummaryEmail, buildQuoteEmailPayload]);

	const persistGuestSessionAndRedirectToLogin = useCallback(async ({ forceLoginPrompt = false } = {}) => {
		writeStudyBuilderGuestSession({
			selections: fitSelections,
			phase: 'build',
			revealEstimateAfterLogin: true,
			quote: {
				lineItems,
				sampleInfo,
				contactInfo,
			},
		});
		const returnPath = `${window.location.origin}/home/build-a-study`;
		localStorage.setItem('returnUrl', returnPath);

		try {
			const response = await postStudyBuilderDraft(buildEstimateRequestBody()).unwrap();
			if (response?.draftToken) {
				writeStudyBuilderDraftToken(response.draftToken);
			}
		} catch {
			// Guest session fallback; redirect even when draft save fails.
		}

		const promptQuery = forceLoginPrompt ? '&prompt=login' : '';
		history.push(`/login?return_url=${encodeURIComponent(returnPath)}${promptQuery}`);
	}, [
		buildEstimateRequestBody,
		contactInfo,
		fitSelections,
		history,
		lineItems,
		postStudyBuilderDraft,
		sampleInfo,
	]);

	const handleAccountSignInAgain = useCallback(async () => {
		if (isAuthed) {
			try {
				const result = await refetchCurrentUser();
				const merged = mergeContactSources(result?.data, auth0User, quoteDraftRef.current.contactInfo);
				if (hasContactInfo(merged)) {
					setContactInfo(merged);
					return;
				}
			} catch {
				// Fall through to forced login.
			}
		}
		await persistGuestSessionAndRedirectToLogin({ forceLoginPrompt: true });
	}, [auth0User, isAuthed, persistGuestSessionAndRedirectToLogin, refetchCurrentUser]);

	const handleSeekEstimateClick = useCallback(async () => {
		if (!canSeeEstimate) {
			setSeekEstimateAttempted(true);
			return;
		}
		if (!isAuthed) {
			await persistGuestSessionAndRedirectToLogin();
			return;
		}
		if (totalSamples < 1) {
			setSeekEstimateAttempted(true);
			return;
		}

		submitEstimateAndReveal();
	}, [
		isAuthed,
		canSeeEstimate,
		persistGuestSessionAndRedirectToLogin,
		submitEstimateAndReveal,
		totalSamples,
	]);

	const seekEstimateBusy = estimateLoading || draftSaving;

	const updateLine = (id, patch) => {
		setLineItems((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
	};

	const addLine = () => {
		setLineItems((rows) => [...rows, { id: `ln-${Date.now()}`, species: '', matrix: '', matrixCategory: '', matrixType: '', count: '', otherNotes: '' }]);
	};

	const removeLine = (id) => {
		setLineItems((rows) => (rows.length <= 1 ? rows : rows.filter((r) => r.id !== id)));
	};

	if (quoteRevealed) {
		return (
			<Box data-testid="quote-wizard">
				<Grid container spacing={3} alignItems="flex-start">
					<Grid item xs={12} md={7}>
						<Paper
							elevation={0}
							sx={{
								p: { xs: 3, md: 5 },
								borderRadius: 3,
								border: `1px solid ${Brand.precisionGrey}`,
								overflow: 'hidden',
								textAlign: 'center',
								'&::before': studyBuilderGradientTopSx,
							}}
						>
							<Box sx={{ pt: 2 }}>
								<CheckCircleOutline sx={{ fontSize: 56, color: Brand.livingPurple, mb: 1.5 }} />
								<Typography variant="h4" sx={{ fontWeight: 800, color: Brand.darkModeBlue, mb: 1 }}>
									Here is your indicative estimate
								</Typography>
								<Typography variant="body1" sx={{ color: Brand.molecularBlue, mb: 2, lineHeight: 1.75, maxWidth: 560, mx: 'auto' }}>
									Thank you, <strong>{contactInfo.firstName}</strong>. Someone from our team will be in touch to confirm scope, timelines,
									and final pricing.
								</Typography>

								{quoteSummaryEmailSending && !quoteSummaryEmailSent ? (
									<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 2 }}>
										<CircularProgress size={22} sx={{ color: Brand.livingPurple }} />
										<Typography variant="body2" sx={{ color: Brand.molecularBlue }}>
											Sending a copy of your summary and estimate to <strong>{contactInfo.email}</strong>…
										</Typography>
									</Box>
								) : null}
								{quoteSummaryEmailSent ? (
									<Typography variant="body2" sx={{ color: Brand.darkModeBlue, mb: 2, maxWidth: 560, mx: 'auto' }}>
										We emailed a copy of this summary and indicative estimate to <strong>{contactInfo.email}</strong>.
									</Typography>
								) : null}
								{quoteSummaryEmailError && !quoteSummaryEmailSent ? (
									<Alert
										severity="warning"
										sx={{ mb: 2, textAlign: 'left', maxWidth: 560, mx: 'auto' }}
										action={
											<Button color="inherit" size="small" onClick={handleRetryQuoteEmail} sx={{ fontWeight: 700 }}>
												Retry
											</Button>
										}
									>
										We could not email your summary just now. You can try again, or save this page for your records.
									</Alert>
								) : null}

								<Box
									sx={{
										borderRadius: 3,
										p: { xs: 2.5, md: 3.5 },
										mb: 3,
										background: `linear-gradient(135deg, rgba(40, 74, 111, 0.12) 0%, rgba(133, 93, 163, 0.1) 50%, rgba(255, 79, 88, 0.08) 100%)`,
										border: `1px solid ${Brand.precisionGrey}`,
									}}
								>
									<Typography variant="overline" sx={{ color: Brand.molecularBlue, fontWeight: 700, letterSpacing: 2 }}>
										Estimated study investment
									</Typography>
									<Typography
										data-testid="quote-confirmed-estimated-cost"
										variant="h3"
										sx={{ fontWeight: 800, color: Brand.darkModeBlue, my: 1.5, letterSpacing: '-0.03em' }}
									>
										{estimatedCostDisplay}
									</Typography>
									<Typography variant="body2" sx={{ color: Brand.molecularBlue }}>
										{quotePresentation?.kind === 'referToSales' ? (
											<>
												We could not show an indicative price for your account online. Our team will follow up to confirm scope,
												timelines, and pricing · target completion around <strong>{estimatedCompletionDisplay}</strong>
											</>
										) : quotePresentation?.tierBounds ? (
											<>
												Based on the <strong>
													{quotePresentation.tierBounds.lo}–{quotePresentation.tierBounds.hi}
												</strong>{' '}
												sample range for your study size (indicative) · target completion around{' '}
												<strong>{estimatedCompletionDisplay}</strong>
											</>
										) : quotePresentation?.kind === 'openEnded' ? (
											<>
												Based on <strong>{totalSamples}</strong> total samples across your matrix lines · indicative minimum shown ·
												target completion around <strong>{estimatedCompletionDisplay}</strong>
											</>
										) : (
											<>
												Based on <strong>{totalSamples}</strong> total samples across your matrix lines · target completion is around{' '}
												<strong>{estimatedCompletionDisplay}</strong>
											</>
										)}
									</Typography>
								</Box>

								<Button variant="outlined" onClick={onBack} sx={{ borderColor: Brand.molecularBlue, color: Brand.molecularBlue, fontWeight: 600 }}>
									Back to selections
								</Button>
							</Box>
						</Paper>
					</Grid>
					<Grid item xs={12} md={5}>
						<StudySummaryCard
							fitSelections={fitSelections}
							primaryProduct={primaryProduct}
							sampleInfo={{
								...sampleInfo,
								numberOfSamples: String(totalSamples),
							}}
							totalSampleCount={totalSamples}
							quoteSampleBasisDisplay={quoteSampleBasisDisplay}
							estimatedCostDisplay={estimatedCostDisplay}
							estimatedCompletionDisplay={estimatedCompletionDisplay}
							showEstimatedCost
						/>
					</Grid>
				</Grid>
			</Box>
		);
	}

	return (
		<Box data-testid="quote-wizard">
			<Paper
				elevation={0}
				sx={{
					mb: 3,
					borderRadius: 3,
					border: `1px solid ${Brand.precisionGrey}`,
					background: Brand.pureWhite,
					boxShadow: '0 12px 40px rgba(0, 29, 56, 0.06)',
					overflow: 'hidden',
					'&::before': studyBuilderGradientTopSx,
				}}
			>
				<Box sx={{ p: { xs: 2.5, md: 4 }, pt: { xs: 2, md: 3 } }}>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 1 }}>
						<Box>
							<Typography
								variant="h4"
								component="h2"
								sx={{
									fontWeight: 800,
									color: Brand.darkModeBlue,
									fontSize: { xs: '1.35rem', sm: '1.65rem' },
									letterSpacing: '-0.02em',
								}}
							>
								Study details & quote
							</Typography>
						</Box>
					</Box>
				</Box>
			</Paper>

			<Grid container spacing={3}>
				<Grid item xs={12} md={7}>
					<Paper
						elevation={0}
						sx={{
							p: { xs: 2.5, md: 3 },
							borderRadius: 3,
							border: `1px solid ${Brand.precisionGrey}`,
							background: `linear-gradient(180deg, ${Brand.pureWhite} 0%, rgba(242, 250, 252, 0.5) 100%)`,
							boxShadow: '0 4px 24px rgba(0, 29, 56, 0.04)',
						}}
					>
						<Box data-testid="wizard-step-sample-info">
							<StudyBuilderSectionTitle title="Shipping & handling" />
							<TextField
								fullWidth
								select
								label="Sample volume"
								value={sampleInfo.sampleVolume}
								onChange={(e) => setSampleInfo((s) => ({ ...s, sampleVolume: e.target.value }))}
								margin="normal"
								inputProps={{ 'data-testid': 'sample-volume-select' }}
							>
								{['50μl', '100μl', '200μl', '500μl', '1ml'].map((v) => (
									<MenuItem key={v} value={v}>
										{v}
									</MenuItem>
								))}
							</TextField>
							<TextField
								fullWidth
								select
								label="Sample shipping"
								value={sampleInfo.sampleShipping}
								onChange={(e) => setSampleInfo((s) => ({ ...s, sampleShipping: e.target.value }))}
								margin="normal"
								inputProps={{ 'data-testid': 'sample-shipping-select' }}
							>
								{['Standard', 'Expedited', 'Customer arranged'].map((v) => (
									<MenuItem key={v} value={v}>
										{v}
									</MenuItem>
								))}
							</TextField>
							<TextField
								fullWidth
								type="date"
								label="Planned shipping date"
								InputLabelProps={{ shrink: true }}
								value={sampleInfo.plannedShippingDate}
								onChange={(e) => setSampleInfo((s) => ({ ...s, plannedShippingDate: e.target.value }))}
								margin="normal"
								inputProps={{ 'data-testid': 'planned-shipping-date' }}
							/>

						<StudyBuilderSectionTitle title="Your account" />
						{isAuthed ? (
							<Paper
								variant="outlined"
								data-testid="quote-account-identity"
								sx={{ p: 2, mb: 2, borderColor: Brand.precisionGrey, background: 'rgba(40, 74, 111, 0.04)' }}
							>
								{isProfileLoading ? (
									<Box
										data-testid="quote-account-loading"
										sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
									>
										<CircularProgress size={18} sx={{ color: Brand.molecularBlue }} />
										<Typography variant="body2" sx={{ color: Brand.darkModeBlue }}>
											Loading your account profile...
										</Typography>
									</Box>
								) : contactReady ? (
									<Typography variant="body1" sx={{ color: Brand.darkModeBlue, lineHeight: 1.7 }}>
										Your estimate will be sent to{' '}
										<strong>
											{displayContactInfo.firstName} {displayContactInfo.lastName}
										</strong>{' '}
										at <strong>{displayContactInfo.email}</strong>.
									</Typography>
								) : (
									<Box data-testid="quote-account-error">
										<Typography variant="body2" sx={{ color: Brand.activatedRed, fontWeight: 600, mb: 1.5 }}>
											We could not load your account profile. Try again, or sign in again to continue.
										</Typography>
										<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
											<Button
												data-testid="quote-account-sign-in-again"
												variant="outlined"
												size="small"
												onClick={handleAccountSignInAgain}
												disabled={draftSaving || currentUserFetching}
												sx={{
													borderColor: Brand.molecularBlue,
													color: Brand.molecularBlue,
													fontWeight: 600,
													borderRadius: 2,
													textTransform: 'none',
												}}
											>
												Sign in again
											</Button>
										</Box>
									</Box>
								)}
							</Paper>
						) : (
							<Paper
								variant="outlined"
								data-testid="quote-guest-sign-in-prompt"
								sx={{ p: 2, mb: 2, borderColor: Brand.molecularBlue, background: 'rgba(40, 74, 111, 0.04)' }}
							>
								<Typography variant="body1" sx={{ color: Brand.darkModeBlue, lineHeight: 1.7, mb: 1.5 }}>
									Sign in or register to receive your estimate. Your study selections and sample logistics will be saved
									while you log in.
								</Typography>
								<Button
									data-testid="quote-guest-sign-in"
									variant="contained"
									size="small"
									onClick={persistGuestSessionAndRedirectToLogin}
									disabled={draftSaving}
									sx={{
										background: Brand.molecularBlue,
										fontWeight: 700,
										borderRadius: 2,
										boxShadow: 'none',
										textTransform: 'none',
										'&:hover': { background: Brand.darkModeBlue },
									}}
								>
									Sign in
								</Button>
							</Paper>
						)}
						</Box>

						{seekEstimateValidationMessage ? (
							<Alert severity="warning" sx={{ mt: 2, mb: 1 }} data-testid="quote-seek-validation-hint">
								{seekEstimateValidationMessage}
							</Alert>
						) : null}
						{estimateFetchErrorMessage ? (
							<Alert severity="error" sx={{ mt: 2, mb: 1 }} data-testid="quote-estimate-fetch-error">
								{estimateFetchErrorMessage}
							</Alert>
						) : null}

						<EstimateSubmissionSummary lines={submissionSummaryLines} />

						<SampleCountCallout
							totalSamples={totalSamples}
							quoteSampleBasisDisplay={quoteSampleBasisDisplay}
						/>

						<Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
							<Button
								variant="outlined"
								onClick={onBack}
								sx={{
									borderColor: Brand.precisionGrey,
									color: Brand.molecularBlue,
									fontWeight: 600,
									borderRadius: 2,
									'&:hover': { borderColor: Brand.molecularBlue, background: 'rgba(40, 74, 111, 0.06)' },
								}}
							>
								Back to selections
							</Button>
							<Button
								data-testid="get-your-quote"
								variant="contained"
								onClick={handleSeekEstimateClick}
								disabled={seekEstimateBusy}
								sx={{
									background: Brand.activatedRed,
									fontWeight: 700,
									borderRadius: 2,
									boxShadow: 'none',
									'&:hover': { background: Brand.insightfulOrange },
									...(!canSeeEstimate && { opacity: 0.55 }),
								}}
							>
								{seekEstimateBusy ? (
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
										<CircularProgress size={18} sx={{ color: Brand.pureWhite }} />
										{draftSaving ? 'Saving selections...' : 'Loading estimate...'}
									</Box>
								) : (
									'See your estimate'
								)}
							</Button>
						</Box>
					</Paper>
				</Grid>

				<Grid item xs={12} md={5}>
					<StudySummaryCard
						fitSelections={fitSelections}
						primaryProduct={primaryProduct}
						sampleInfo={{
							...sampleInfo,
							numberOfSamples: String(totalSamples),
						}}
						totalSampleCount={totalSamples}
						quoteSampleBasisDisplay={quoteSampleBasisDisplay}
						estimatedCostDisplay={estimatedCostDisplay}
						estimatedCompletionDisplay={estimatedCompletionDisplay}
						showEstimatedCost={false}
					/>
				</Grid>
			</Grid>
		</Box>
	);
}
